import React, { createContext, useContext, useState, useEffect } from 'react';
import {  getAllSubjects } from '../subjects/subject-form/subjectAction';
import { getAllTeacher } from '../../../teachers/pages/teacher-data/teacherAction';

// Create the context
const SchoolDataContext = createContext();

// Custom hook to use the context
export const useSchoolData = () => {
  const context = useContext(SchoolDataContext);
  if (!context) {
    throw new Error('useSchoolData must be used within a SchoolDataProvider');
  }
  return context;
};

// Provider component
export const SchoolDataProvider = ({ children }) => {
  const [schoolData, setSchoolData] = useState({
    subjects: [],
    teachers: [],
    loading: {
      subjects: true,
      teachers: true
    },
    errors: {
      subjects: null,
      teachers: null
    }
  });

  // Helper function to update loading state
  const setLoading = (key, isLoading) => {
    setSchoolData(prev => ({
      ...prev,
      loading: {
        ...prev.loading,
        [key]: isLoading
      }
    }));
  };

  // Helper function to set error
  const setError = (key, error) => {
    setSchoolData(prev => ({
      ...prev,
      errors: {
        ...prev.errors,
        [key]: error
      }
    }));
  };

  // Fetch all subjects
  const fetchSubjects = async () => {
    try {
      setLoading('subjects', true);
      setError('subjects', null);
      
      const response = await getAllSubjects();
      setSchoolData(prev => ({
        ...prev,
        subjects: response?.data?.data || []
      }));
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setError('subjects', error.message);
    } finally {
      setLoading('subjects', false);
    }
  };

  // Fetch all teachers
  const fetchTeachers = async () => {
    try {
      setLoading('teachers', true);
      setError('teachers', null);
      
      const response = await getAllTeacher({ show_class: true });
      setSchoolData(prev => ({
        ...prev,
        teachers: response?.data?.data || []
      }));
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setError('teachers', error.message);
    } finally {
      setLoading('teachers', false);
    }
  };

  const refreshData = {
    subjects: fetchSubjects,
    teachers: fetchTeachers,
    all: () => {
      fetchSubjects();
      fetchTeachers();
    }
  };

  // Check if all data is loaded
  const isAllDataLoaded = !Object.values(schoolData.loading).some(loading => loading);

  // Check if there are any errors
  const hasErrors = Object.values(schoolData.errors).some(error => error !== null);

  // Initial data fetch
  useEffect(() => {
    fetchSubjects();
    fetchTeachers();
  }, []);

  const contextValue = {
    ...schoolData,
    refreshData,
    isAllDataLoaded,
    hasErrors
  };

  return (
    <SchoolDataContext.Provider value={contextValue}>
      {children}
    </SchoolDataContext.Provider>
  );
};

export default SchoolDataContext;